import { useQuery } from '@apollo/client/react';
import { Badge, Box, Button, Center, HStack, Heading, Link, SkeletonText, StackProps, Text, VStack, Wrap } from '@chakra-ui/react';
import { UserAvatar } from '@src/components/sco/Account/UserAvatar';
import { REQUEST_BUILDER_GROUP_REQUEST_FRAGMENT } from '@src/components/sco/Requests/RequestBuilder';
import { NextLink } from '@src/components/sco/utilities/NextLink';
import { gql } from '@src/lib/gql';
import { useRouter } from 'next/navigation';
import React from 'react';
import { LuArrowUpRight } from 'react-icons/lu';
export type RequestListCardProps = {
  groupRequestId: string;
  showTags?: boolean;
  showRequestingUser?: boolean;
  stackProps?: StackProps;
  titleSize?: string;
  ctaType?: 'manage' | 'open';
};
export const RequestListCard: React.FC<RequestListCardProps> = props => {
  const router = useRouter();
  const query = useQuery(gql`
      query ($groupRequestId: String!) {
        groupRequest(groupRequestId: $groupRequestId) {
          id
          ${REQUEST_BUILDER_GROUP_REQUEST_FRAGMENT}
          groupRequestId
          title
          canUserEdit
          requestingCompany {
            id
            companyId
            name
            fundingGoalInDollars
          }
          requestingUser {
            id
            userId
            displayName
            firstName
            lastName
            pictureUrl
            title
            ${UserAvatar.fragments.ScoUser()}
          }
          tags {
            id
            tagId
            slug
            title
            tagTypeKey
          }
        }
      }
    `, {
    variables: {
      groupRequestId: props.groupRequestId
    }
  });
  const groupRequest = query.data?.groupRequest;
  if (query.loading) {
    return <Box bgColor={'blackAlpha.300'} borderRadius={'md'} padding={4} width={'full'}>
        <SkeletonText height={'5'} variant={'shine'} width={'full'} css={{
        '--start-color': 'colors.colorPalette.fg/50',
        '--end-color': 'colors.colorPalette.fg/100'
      }} />
      </Box>;
  }
  if (query.error || !groupRequest) {
    return <Center padding={4}>
        <Text>
          {'Error: '}
          {query.error?.toString() || 'Not Found'}
        </Text>
      </Center>;
  }
  const cardHref = groupRequest?.canUserEdit ? `/requests/manage/${props.groupRequestId}` : `/requests/${props.groupRequestId}`;
  return <VStack align={'stretch'} bgColor={'colorPalette.bg'} border={'1px solid'} borderColor={'colorPalette.border'} borderRadius={'md'} boxShadow={'xs'} cursor={'pointer'} gap={0} justify={'center'} width={'full'} _hover={{
    bgColor: 'colorPalette.subtle'
  }} onClick={() => {
    router.push(cardHref);
  }} {...props.stackProps}>
      <VStack align={'stretch'} gap={2} padding={4}>
        <HStack gap={2} justify={'space-between'}>
          <Text as={'span'} color={'colorPalette.fg/50'} fontSize={'2xs'} textStyle={'label'}>
            Goal
          </Text>
          {groupRequest.canUserEdit && <>
              {props.ctaType === 'open' ? <Button asChild size={'xs'} variant={'outline'}>
                  <NextLink href={cardHref} onClick={ev => {
              ev.stopPropagation();
            }}>
                    Open
                    <LuArrowUpRight />
                  </NextLink>
                </Button> : null}
            </>}
        </HStack>
        <Box>
          <Heading fontSize={props.titleSize || 'md'} lineHeight={'short'}>
            <Link asChild>
              <NextLink href={cardHref}>{groupRequest.displayTitle}</NextLink>
            </Link>
          </Heading>
        </Box>
        {props.showTags && groupRequest.tags && <Wrap gap={1} maxHeight={'5em'} overflowY={'scroll'}>
            {groupRequest.tags?.map((tag, idx) => <Badge key={idx} animation={`2s ease-in bgOpacity`} bgColor={'blackAlpha.500'} color={'colorPalette.border'} fontFamily={'Poppins'} fontSize={'10px'} fontWeight={600} lineHeight={1} opacity={parseInt(tag[1]) / 100} paddingX={1} paddingY={1} variant={'solid'} css={{
          '--bg-opacity-from': 'var(--chakra-colors-primaryAlpha-200)',
          '--bg-opacity-to': 'var(--chakra-colors-primaryAlpha-400)'
        }}>
                {tag.title}
              </Badge>)}
          </Wrap>}
        {/* {groupRequest?.canUserEdit && (
          <Box paddingY={2}>
            <RequestStatusCard groupRequest={groupRequest} groupRequestId={props.groupRequestId} />
          </Box>
         )} */}
        {props.showRequestingUser && <HStack borderTop={'1px solid'} borderTopColor={'whiteAlpha.200'} gap={2} paddingTop={4}>
            <UserAvatar avatarProps={{
          size: 'xs'
        }} userId={groupRequest.requestingUser?.userId} />
            <Heading fontSize={'14px'}>{groupRequest.requestingUser?.displayName}</Heading>
          </HStack>}
      </VStack>
    </VStack>;
};